import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Info, TrendingUp, TrendingDown, Minus, Plus, RefreshCw } from "lucide-react";
import { fmtEur, fmtEurS, fmtPct, fmtPctS, fmtShort, signCls } from "./caDetailUtils";

const TT = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 11 };
const pct1 = (v) => (v * 100).toFixed(1).replace(".", ",") + " %";

export function Card({ title, sub, right, children, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-[0_1px_3px_rgba(15,23,42,.04)] flex flex-col" data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div><h3 className="font-display text-sm font-bold text-slate-900">{title}</h3>{sub && <p className="text-[10px] text-slate-500">{sub}</p>}</div>
        {right}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

export function WeekdayWeight({ weekday }) {
  const data = weekday.map(w => ({ name: w.jour, "Poids CA réalisé": +(w.pct * 100).toFixed(1), "Poids historique (N-1)": +(w.pct_n1 * 100).toFixed(1) }));
  return (
    <Card title="Poids du CA par jour (sélection)" testid="cad-w-weekday">
      <div className="h-48">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false}/>
            <YAxis tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} unit="%"/>
            <Tooltip contentStyle={TT} formatter={(v) => v.toFixed(1).replace(".", ",") + " %"}/>
            <Legend wrapperStyle={{ fontSize: 10 }} iconType="circle"/>
            <Bar dataKey="Poids CA réalisé" fill="#2563EB" radius={[3, 3, 0, 0]}/>
            <Bar dataKey="Poids historique (N-1)" fill="#93C5FD" radius={[3, 3, 0, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

export function WeeksWeight({ refm }) {
  const weeks = refm?.weeks || [];
  return (
    <Card title="Poids des semaines dans le mois" sub={refm ? refm.label : ""} testid="cad-w-weeks">
      {weeks.length === 0 ? <p className="text-xs text-slate-400 py-6 text-center">Aucune semaine</p> : (
        <div className="space-y-2.5 mt-1">
          {weeks.map(w => (
            <div key={w.semaine} className="flex items-center gap-2" data-testid={`cad-week-${w.semaine}`}>
              <span className="text-[11px] font-semibold text-slate-600 w-9">{w.label}</span>
              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden relative">
                <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(100, w.pct * 100)}%` }}/>
                {w.pct_n1 != null && <div className="absolute top-0 h-full w-0.5 bg-slate-500" style={{ left: `${Math.min(100, w.pct_n1 * 100)}%` }} title={`N-1 : ${pct1(w.pct_n1)}`}/>}
              </div>
              <span className="text-[11px] font-bold text-slate-800 w-12 text-right tabular-nums">{pct1(w.pct)}</span>
              <span className={`text-[10px] w-14 text-right tabular-nums ${w.pct_n1 == null ? "text-slate-300" : signCls(w.pct - w.pct_n1)}`}>{w.pct_n1 == null ? "—" : fmtPctS(w.pct - w.pct_n1, 1).replace(" %", " pt")}</span>
            </div>
          ))}
          <p className="text-[10px] text-slate-400 flex items-center gap-1"><span className="inline-block w-2 h-2 bg-slate-500"/> repère N-1 · écart en points vs N-1</p>
        </div>
      )}
    </Card>
  );
}

export function Contributions({ contributions }) {
  const [tab, setTab] = useState("corner");
  const c = contributions?.[tab] || { positifs: [], negatifs: [] };
  const items = [...c.negatifs, ...c.positifs].sort((a, b) => Math.abs(b.ecart) - Math.abs(a.ecart));
  return (
    <Card title="Contribution à l'écart (Top 5)" testid="cad-w-contrib" right={
      <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
        {[["corner", "Corner"], ["semaine", "Semaines"], ["jour", "Jours"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`cad-contrib-${k}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${tab === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>)}
      </div>}>
      <div className="grid grid-cols-2 gap-3 mt-1">
        {[["negatifs", "Négatifs", c.negatifs], ["positifs", "Positifs", c.positifs]].map(([k, l, arr]) => (
          <div key={k}>
            <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${k === "positifs" ? "text-emerald-600" : "text-rose-600"}`}>{l}</p>
            {arr.length === 0 && <p className="text-[10px] text-slate-400">—</p>}
            {arr.map(i => (
              <div key={i.key} className="flex items-center justify-between py-1 border-b border-slate-50 text-[11px]" data-testid={`cad-contrib-item-${i.key}`}>
                <span className="flex items-center gap-1.5 truncate"><span className={`w-4 h-4 rounded-full grid place-items-center ${k === "positifs" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{k === "positifs" ? <Plus className="w-2.5 h-2.5"/> : <Minus className="w-2.5 h-2.5"/>}</span><span className="truncate text-slate-700">{i.label}</span></span>
                <span className={`font-bold tabular-nums ${signCls(i.ecart)}`}>{fmtEurS(i.ecart)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function Day14h({ day14 }) {
  const d = day14;
  const pct = d ? Math.min(1, d.pct || 0) : 0;
  const ahead = d && d.has_data ? d.pct >= 1 : null;
  return (
    <Card title="Dynamique du jour (CA à 14h)" sub={d ? d.label : ""} testid="cad-w-day14">
      {!d ? <p className="text-xs text-slate-400 py-6 text-center">Aucune donnée</p> : (
        <div className="flex items-center gap-4">
          <div className="relative w-28 h-28 shrink-0">
            <ResponsiveContainer>
              <PieChart><Pie data={[{ v: pct }, { v: 1 - pct }]} dataKey="v" innerRadius={38} outerRadius={50} startAngle={90} endAngle={-270} stroke="none">
                <Cell fill={ahead ? "#16A34A" : d.has_data ? "#2563EB" : "#CBD5E1"}/><Cell fill="#EEF2F7"/></Pie></PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center text-center"><div><div className="text-lg font-bold text-slate-900 leading-none" data-testid="cad-day14-pct">{d.has_data ? Math.round(d.pct * 100) + " %" : "—"}</div><div className="text-[9px] text-slate-500">de l'obj. 14h</div></div></div>
          </div>
          <div className="flex-1 space-y-1 text-[11px]">
            <div className="flex justify-between"><span className="text-slate-500">CA à 14h</span><b>{d.has_data ? fmtEur(d.ca_14h) : "—"}</b></div>
            <div className="flex justify-between"><span className="text-slate-500">Objectif 14h</span><b>{fmtEur(d.objectif_14h)}</b></div>
            <div className="flex justify-between"><span className="text-slate-500">Objectif jour</span><b>{fmtEur(d.objectif_jour)}</b></div>
            <div className="flex justify-between"><span className="text-slate-500">Projection journalière</span><b>{d.projection_jour != null ? fmtEur(d.projection_jour) : "—"}</b></div>
            <div className="flex justify-between"><span className="text-slate-500">Reste à faire</span><b>{fmtEur(d.reste)}</b></div>
            <div className={`mt-2 rounded-lg px-2 py-1.5 text-[10px] flex items-start gap-1.5 ${!d.has_data ? "bg-slate-50 text-slate-500" : ahead ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="cad-day14-msg">
              <Info className="w-3 h-3 mt-0.5 shrink-0"/>
              {!d.has_data ? `Aucun CA 14h remonté par les corners (messagerie Roy) pour ce jour. Objectif 14h estimé à ${Math.round(d.ratio * 100)} % de l'objectif journalier.`
                : ahead ? `Rythme en avance de ${fmtPctS(d.pct - 1)} par rapport au rythme habituel (${d.nb_corners_14h} corners).`
                : `Rythme en retard de ${fmtPctS(d.pct - 1)} par rapport au rythme habituel (${d.nb_corners_14h} corners).`}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export function ProjectionChart({ refm }) {
  const curve = refm?.curve || [];
  const ok = refm && refm.projection_pct >= 0;
  return (
    <Card title="Projection fin de mois" sub={refm?.label} testid="cad-w-projection" right={refm && (
      <div className="text-right"><div className="text-sm font-bold text-slate-900" data-testid="cad-proj-value">{fmtEur(refm.projection)}</div><div className={`text-[10px] font-semibold ${signCls(refm.projection_pct)}`}>{fmtPctS(refm.projection_pct)} vs objectif</div></div>)}>
      <div className="h-40">
        <ResponsiveContainer>
          <LineChart data={curve} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false}/>
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} interval={4}/>
            <YAxis tickFormatter={fmtShort} tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} width={60}/>
            <Tooltip contentStyle={TT} formatter={(v) => fmtEur(v)} labelFormatter={(l) => `Jour ${l}`}/>
            <Legend wrapperStyle={{ fontSize: 10 }} iconType="plainline"/>
            <Line type="monotone" dataKey="objectif" name="Objectif" stroke="#94A3B8" strokeDasharray="4 3" dot={false} strokeWidth={2}/>
            <Line type="monotone" dataKey="projete" name="Projection" stroke={ok ? "#16A34A" : "#F59E0B"} strokeDasharray="2 3" dot={false} strokeWidth={2}/>
            <Line type="monotone" dataKey="realise" name="Réalisé" stroke="#2563EB" dot={false} strokeWidth={2.5} connectNulls={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
      {refm && <p className={`mt-1 rounded-lg px-2 py-1 text-[10px] flex items-center gap-1.5 ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
        {ok ? <TrendingUp className="w-3 h-3"/> : <TrendingDown className="w-3 h-3"/>} Au rythme actuel ({fmtEur(refm.avg_day)}/jour), l'objectif du mois sera {ok ? "atteint" : "manqué"} de {fmtEur(Math.abs(refm.projection - refm.objectif))}.
      </p>}
    </Card>
  );
}

export function WeatherImpact({ impact, status, onSync, canSync, syncing }) {
  const cats = impact?.cats || [];
  const max = Math.max(1, ...cats.map(c => c.ca_moyen || 0));
  return (
    <Card title="Impact météo sur le CA" sub={impact?.n ? `${impact.n} corner-jours avec météo · couverture ${Math.round((impact.coverage || 0) * 100)} %` : "Aucune donnée météo pour la sélection"} testid="cad-w-weather" right={
      <div className="flex items-center gap-2">
        {status?.running && <span className="text-[10px] text-blue-600 animate-pulse" data-testid="cad-weather-progress">{status.progress || "Synchronisation…"}</span>}
        {!status?.running && status?.last_date && <span className="text-[10px] text-slate-400">Météo à jour au {status.last_date}</span>}
        {canSync && <button onClick={onSync} disabled={syncing || status?.running} data-testid="cad-weather-sync" className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-600 border border-slate-200 rounded-lg px-2 py-1 hover:bg-slate-50 disabled:opacity-50"><RefreshCw className={`w-3 h-3 ${status?.running ? "animate-spin" : ""}`}/> Récupérer la météo</button>}
      </div>}>
      {cats.length === 0 ? <p className="text-xs text-slate-400 py-6 text-center">Lancez la récupération météo pour analyser l'impact du temps sur le CA.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-1">
          {cats.map(c => (
            <div key={c.cat} className="rounded-lg border border-slate-100 p-3 flex flex-col gap-1" data-testid={`cad-weather-cat-${c.cat}`}>
              <div className="flex items-center justify-between"><span className="text-lg leading-none">{c.emoji}</span><span className="text-[10px] text-slate-500">{pct1(c.share)} des jours</span></div>
              <div className="text-[11px] font-bold text-slate-800">{c.label}{c.tmax_moy != null && <span className="text-slate-400 font-normal"> · {Math.round(c.tmax_moy)}°</span>}</div>
              <div className="text-[13px] font-bold text-slate-900 tabular-nums">{fmtEur(c.ca_moyen)}<span className="text-[10px] text-slate-400 font-normal"> / corner-jour</span></div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${(c.ca_moyen / max) * 100}%` }}/></div>
              <div className={`text-[10px] font-semibold ${c.perf == null ? "text-slate-400" : c.perf >= 1 ? "text-emerald-600" : "text-rose-600"}`}>{c.perf == null ? "—" : `${fmtPct(c.perf, 0)} de l'objectif`}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function ExtraStats({ by_dr, weekday, stats }) {
  const [tab, setTab] = useState("dr");
  const ranked = [...weekday].filter(w => w.perf != null).sort((a, b) => b.perf - a.perf);
  return (
    <Card title="Statistiques complémentaires" testid="cad-w-extra" right={
      <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
        {[["dr", "Par DR"], ["jours", "Jours"], ["stats", "Stats"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`cad-extra-${k}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${tab === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{l}</button>)}
      </div>}>
      {tab === "dr" && (
        <div className="space-y-2 mt-1">
          {by_dr.slice(0, 8).map(d => (
            <div key={d.dr} className="flex items-center gap-2 text-[11px]" data-testid={`cad-dr-${d.dr}`}>
              <span className="w-24 truncate font-semibold text-slate-700">{d.dr}</span>
              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${d.pct >= 1 ? "bg-emerald-500" : d.pct >= 0.95 ? "bg-amber-400" : "bg-blue-600"}`} style={{ width: `${Math.min(100, (d.pct || 0) * 100)}%` }}/></div>
              <span className="w-16 text-right tabular-nums text-slate-600">{fmtShort(d.realise)}</span>
              <span className={`w-12 text-right tabular-nums font-bold ${d.pct >= 1 ? "text-emerald-600" : d.pct >= 0.95 ? "text-amber-600" : "text-rose-600"}`}>{fmtPct(d.pct, 0)}</span>
            </div>
          ))}
          {by_dr.length === 0 && <p className="text-xs text-slate-400 py-6 text-center">Aucune donnée</p>}
        </div>
      )}
      {tab === "jours" && (
        <div className="mt-1 space-y-1">
          {ranked.map((w, i) => (
            <div key={w.jour} className="flex items-center justify-between text-[11px] py-1 border-b border-slate-50" data-testid={`cad-rank-${w.jour}`}>
              <span className="flex items-center gap-2"><span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${i === 0 ? "bg-emerald-100 text-emerald-700" : i === ranked.length - 1 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span><span className="font-semibold text-slate-700">{w.jour_long}</span></span>
              <span className="flex items-center gap-3 tabular-nums"><span className="text-slate-500">{fmtShort(w.realise)}</span><span className={`font-bold ${signCls(w.perf - 1)}`}>{fmtPct(w.perf, 0)}</span></span>
            </div>
          ))}
          {ranked.length > 0 && <p className="text-[10px] text-slate-400 pt-1">Meilleur jour : {ranked[0].jour_long} · Pire jour : {ranked[ranked.length - 1].jour_long} (vs objectif)</p>}
        </div>
      )}
      {tab === "stats" && stats && (
        <div className="grid grid-cols-2 gap-2 mt-1 text-[11px]">
          <div className="rounded-lg bg-emerald-50 p-2"><div className="text-[10px] text-emerald-700">Jours ≥ objectif</div><div className="text-lg font-bold text-emerald-700" data-testid="cad-stat-above">{stats.days_above}</div></div>
          <div className="rounded-lg bg-rose-50 p-2"><div className="text-[10px] text-rose-700">Jours &lt; objectif</div><div className="text-lg font-bold text-rose-700" data-testid="cad-stat-below">{stats.days_below}</div></div>
          <div className="rounded-lg bg-slate-50 p-2 col-span-2"><div className="text-[10px] text-slate-500">Meilleure journée</div>{stats.best_day ? <div><b>{stats.best_day.label}</b> · {fmtEur(stats.best_day.realise)} <span className="text-emerald-600 font-semibold">({fmtPct(stats.best_day.pct, 0)})</span></div> : "—"}</div>
          <div className="rounded-lg bg-slate-50 p-2 col-span-2"><div className="text-[10px] text-slate-500">Série en cours</div>{stats.streak ? <div><b>{stats.streak} jour{stats.streak > 1 ? "s" : ""}</b> consécutif{stats.streak > 1 ? "s" : ""} <span className={stats.streak_up ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{stats.streak_up ? "au-dessus" : "en dessous"} de l'objectif</span></div> : "—"}</div>
        </div>
      )}
    </Card>
  );
}
